import React from "react";
import type { ExistingWorkCode } from "../../../../../models";
import BulkAssign from "../SectionRow/BulkAssign/BulkAssign";
import "./SelectionBar.css";

interface Props {
  scheduleId: number;
  /** How many activities are selected across the whole editor. */
  count: number;
  /** Codes created earlier this session, for the picker. */
  sessionCodes?: ExistingWorkCode[];
  /** Assign a work code to every selected activity. */
  onAssign: (code: string, name: string, color: string) => void;
  /** Untag every selected activity — drops their code + placement (the
   *  zone/level rows stay). */
  onRemove: () => void;
  /** Drop the selection. */
  onClear: () => void;
}

/** The single bulk-action surface: a compact bar floating at the bottom of the
 *  Structure tab. Always present (so the actions are discoverable) but disabled
 *  until there's a selection. Overlays content — no layout height. */
const SelectionBar: React.FC<Props> = ({
  scheduleId,
  count,
  sessionCodes,
  onAssign,
  onRemove,
  onClear,
}) => {
  const has = count > 0;
  return (
    <div
      className={`takt-editor-selbar ${has ? "" : "is-empty"}`}
      role="region"
      aria-label="Selection actions"
    >
      {has ? (
        <span className="takt-editor-selbar-sel">
          <span className="takt-editor-selbar-count">✓ {count} selected</span>
          <button type="button" className="takt-editor-selbar-clear" onClick={onClear}>
            Clear
          </button>
        </span>
      ) : (
        <span className="takt-editor-selbar-hint">Select activities to set a code or remove</span>
      )}
      <BulkAssign
        scheduleId={scheduleId}
        count={count}
        onAssign={onAssign}
        extraCodes={sessionCodes}
      />
      <button
        type="button"
        className="takt-editor-selbar-remove"
        onClick={onRemove}
        disabled={!has}
        title="Untag the selected activities — drops their code + placement, back to the untagged rail (the zone/level rows stay)"
      >
        ⊘ Remove from board
      </button>
    </div>
  );
};

export default SelectionBar;
